import {
    ClipboardCheck,
    Users,
    Settings2,
    LineChart,
    Recycle,
    Cog,
    Package,
    Globe2,
    ShieldCheck,
    Droplets,
    Sprout,
    Boxes,
    Bot,
    Layers,
    Workflow,
    TrendingUp,
    Route,
    Network,
    LayoutDashboard,
    Plug,
    CheckCircle2,
    Building2,
    CircleDot,
    Factory,
    Leaf,
    Waves,
    HardHat,
    UtensilsCrossed,
    Biohazard,
    Truck,
} from "lucide-react";

const U = (id) =>
    `https://images.unsplash.com/${id}?q=80&w=1400&auto=format&fit=crop`;
const S = (id) =>
    `https://images.unsplash.com/${id}?q=75&w=800&auto=format&fit=crop`;

export const SECTORS = {
    "facility-management": {
        n: "01",
        overline: "Solution 01 — Integrated Operations",
        title: "Total Facility Management",
        lead: "Hard and soft facility services under one contract, one SLA, one accountable partner.",
        challenge:
            "Fragmented vendors and reactive maintenance drive up cost, downtime and risk.",
        approach:
            "One integrated programme — from full technical audit to 24/7 managed operations and KPI reporting.",
        heroImg: {
            src: U("photo-1486406146926-c627a92ad1ab"),
            alt: "Modern commercial facility under integrated management",
            caption: "Integrated Facility Operations",
        },
        flow: [
            { icon: ClipboardCheck, label: "Audit & characterize", sub: "Technical condition and waste streams assessed asset-wide." },
            { icon: Users, label: "Mobilize teams", sub: "Hard and soft service teams deployed under one plan." },
            { icon: Settings2, label: "Manage operations", sub: "Planned maintenance, 24/7 helpdesk, SLA monitoring." },
            { icon: LineChart, label: "Report & optimize", sub: "KPIs, cost tracking, continuous improvement." },
        ],
        flowTags: ["Audit", "Mobilize", "Manage", "Report"],
        services: [
            { name: "Annual Maintenance Contracts", desc: "Year-round preventive and corrective maintenance under a single agreement.", img: S("photo-1621905251189-08b45d6a269e") },
            { name: "MEP & HVAC Services", desc: "Chilled water, AHU/FCU and ventilation maintained for efficiency and air quality.", img: S("photo-1621905252507-b35492cc74b4") },
            { name: "Civil & Fit-Out Works", desc: "Civil repairs, fit-out works and infrastructure maintenance by certified technicians.", img: S("photo-1504328345606-18bbc8c9d7d1") },
            { name: "Cleaning & Housekeeping", desc: "General and deep cleaning, housekeeping and day-to-day soft services.", img: S("photo-1581578731548-c64695cc6952") },
            { name: "Security & Surveillance", desc: "Manned guarding, CCTV monitoring and access control for people and assets.", img: S("photo-1581092795360-fd1ca04f0952") },
            { name: "Roof Isolation Works", desc: "Waterproofing and roof isolation systems that protect assets from heat, leaks and structural wear.", img: S("photo-1635424710928-0544e8512eae") },
            { name: "Facade Cleaning", desc: "Facade and glass cleaning for commercial buildings — water-fed pole and rope-access teams.", img: S("photo-1603712725038-e9334ae8f39f") },
        ],
        alsoIncluded: [
            "Pest Control",
            "Landscaping & Grounds",
            "Fire & Life-Safety Systems",
            "Disinfection & Sanitation",
            "FM Consultancy",
            "Energy Management",
        ],
        stats: ["Single SLA", "24/7 Helpdesk", "Certified Teams", "Live KPI Reporting"],
    },
    "waste-recovery": {
        n: "02",
        overline: "Solution 02 — Circular Materials",
        title: "Waste Recovery Solutions",
        lead: "Full-spectrum waste management and recycling — food waste, dry general waste and hazardous waste, treated and recovered responsibly.",
        challenge:
            "Mixed streams, inconsistent quality and compliance pressure keep waste costs high and recovery rates low.",
        approach:
            "Three specialist programmes — food waste, dry waste and hazardous waste — each characterized, processed and reported with full traceability.",
        pillars: [
            { icon: UtensilsCrossed, title: "Food Waste Treatment & Diversion", desc: "Organic and food waste diverted from landfill into treatment and recovery." },
            { icon: Recycle, title: "Dry & General Waste Recycling", desc: "Plastics, paper, metals and general dry waste sorted, processed and returned to value." },
            { icon: Biohazard, title: "Hazardous Waste Treatment & Compliant Disposal", desc: "Licensed handling, treatment and compliant disposal of hazardous streams." },
        ],
        heroImg: {
            src: "/assets/resource-recovery.jpg",
            alt: "NOMAS material recovery facility — sorting lines and recovered materials",
            caption: "Material Recovery Operations",
        },
        flow: [
            { icon: ClipboardCheck, label: "Streams audited", sub: "Every stream characterized on site — the data foundation." },
            { icon: Cog, label: "Processing lines", sub: "Material-specific sorting, shredding and refining." },
            { icon: Package, label: "Recovered products", sub: "Specification-grade outputs ready for buyers." },
        ],
        features: [
            { icon: ShieldCheck, title: "Hazardous & Non-Hazardous Processing", desc: "Compliant, traceable processing under strict safety protocols." },
            { icon: Package, title: "Burnt, Expired & Distressed Cargo", desc: "Port and industrial cargo converted into secondary products." },
            { icon: Waves, title: "Marine Cleanup & Processing", desc: "Seashore and bay remediation after oil, chemical or debris spills." },
            { icon: HardHat, title: "Demolition & Fit-Out Removal", desc: "Demolition waste separated into structured recovery streams." },
        ],
        productsTitle: "Tire Recycling & Rubber Valorization",
        productsIntro:
            "End-of-life tires become high-quality, industrial-grade materials — graded, traceable, ready for manufacturing.",
        products: [
            { name: "Rubber Powder", spec: "1–4 mm grade", use: "High-quality graded powder for volume applications.", apps: ["Modified Asphalt", "Sports Surfaces", "Moulded Goods"], industry: "Rubber industry", icon: CircleDot },
            { name: "Crumb Rubber", spec: "30 mesh", use: "Consistent, clean crumb for safety and surface systems.", img: "/assets/rubber-crumb.jpg", apps: ["Playgrounds", "Athletic Tracks", "Safety Flooring"], industry: "Rubber industry", icon: CircleDot },
            { name: "Rubber Powder — Fine Grade", spec: "1–3 mm grade", use: "High-quality fine material for precision compounding.", apps: ["Retreading", "Rubber Mats", "Automotive Parts"], industry: "Rubber / retreading", icon: CircleDot },
            { name: "Recovered Steel Powder", spec: "98% purity", use: "High-purity recovered steel for industrial reuse.", apps: ["Steel Smelting", "Manufacturing Plants"], industry: "Steel industry", icon: Factory },
        ],
        stats: ["Traceable Flows", "Specification-Grade", "Licensed Handling", "Circular Reporting"],
    },
    "alva-oliva": {
        n: "03",
        overline: "Solution 03 — Premium Food Distribution",
        title: "Alva Oliva — Extra Virgin Olive Oil",
        lead: "NOMAS is the authorized distributor of Alva Oliva extra virgin olive oil in the GCC.",
        challenge:
            "Premium EVOO supply in the region lacks consistent quality, provenance and brand backing.",
        approach:
            "Direct from a family producer in Torredonjimeno, Spain — BRC and IFS certified, in formats from 250 ml to 5 L for retail and HORECA.",
        gallery: [
            { src: "/assets/alva-bottles.jpg", alt: "Alva Oliva extra virgin olive oil bottles — 250 ml, 750 ml formats" },
            { src: "/assets/alva-range.jpg", alt: "Alva Oliva full range — glass bottles and tins from 250 ml to 5 L" },
            { src: "/assets/oleicola-quality.jpg", alt: "Certified quality and food safety at the Álvarez mill" },
        ],
        flow: [
            { icon: Sprout, label: "Estate & mill", sub: "Family groves and mill in Torredonjimeno, Spain — since 1996." },
            { icon: ShieldCheck, label: "Certified quality", sub: "BRC and IFS food-safety and quality standards." },
            { icon: Truck, label: "GCC distribution", sub: "NOMAS-managed import, warehousing and delivery." },
        ],
        features: [
            { icon: Droplets, title: "Premium EVOO", desc: "Extra virgin olive oil with a distinctive, award-winning brand identity." },
            { icon: ShieldCheck, title: "Certified Quality", desc: "BRC and IFS certified production — food safety proven, not promised." },
            { icon: Package, title: "Retail & HORECA Formats", desc: "Glass, PET and tin formats from 250 ml to 5 L." },
            { icon: Globe2, title: "GCC Distribution", desc: "Authorized regional distribution with full logistics support." },
        ],
        stats: ["Authorized Distributor", "BRC & IFS Certified", "Est. 1996 — Spain", "Retail & HORECA"],
    },
    "uco-sourcing": {
        n: "04",
        overline: "Solution 04 — Certified Feedstock",
        title: "Used Cooking Oil (UCO) Sourcing & Supply",
        lead: "NOMAS is an international sourcing agent for ISCC-certified UCO — from collection networks to biodiesel refineries.",
        challenge:
            "Refineries need certified, traceable UCO at consistent quality and volume.",
        approach:
            "We aggregate ISCC-certified UCO from commercial kitchens and collectors, and deliver specification-ready feedstock with full documentation.",
        heroImg: {
            src: "/assets/uco-photo.jpg",
            alt: "Used cooking oil being collected into a UCO container in a commercial kitchen",
            caption: "UCO Collection — Commercial Kitchens",
        },
        extraImg: {
            src: "/assets/uco-panel.jpg",
            alt: "Used cooking oil (UCO) — from kitchens to second-generation biofuels: biodiesel, plastics and polymers, oleochemicals, asphalt",
        },
        flow: [
            { icon: UtensilsCrossed, label: "Collection networks", sub: "Commercial kitchens, food processors and aggregators." },
            { icon: ShieldCheck, label: "ISCC certification & QA", sub: "Traceability and quality assured at every handover." },
            { icon: Factory, label: "Refinery delivery", sub: "Specification-compliant feedstock to biodiesel refineries." },
        ],
        features: [
            { icon: ShieldCheck, title: "ISCC Traceability", desc: "Certified chain of custody from collection point to refinery gate." },
            { icon: Network, title: "Collection Networks", desc: "Aggregated supply from kitchens, processors and collectors." },
            { icon: Truck, title: "Export Logistics", desc: "Documentation, freight and compliance handled end to end." },
            { icon: Factory, title: "Refinery Supply Programs", desc: "Consistent volumes and specifications for biofuel producers." },
        ],
        stats: ["ISCC Certified", "Traceable Collection", "Refinery-Ready", "GCC & Export"],
    },
    "advanced-materials": {
        n: "05",
        overline: "Solution 05 — Bio-Based Manufacturing",
        title: "Advanced Materials",
        lead: "Biodegradable biocomposites — from renewable resources and agricultural by-products — engineered as pellets and filament for 3D printing.",
        challenge:
            "Conventional plastics mean fossil feedstocks, high waste and long lead times.",
        approach:
            "Certified-compostable materials tuned for FDM/FFF printers and robotic arms — low-waste, on-demand production.",
        heroImg: {
            src: "/assets/advanced-materials.jpg",
            alt: "From waste to solution — recycled plastic filament feeding a 3D printer",
            caption: "From Waste to Solution",
        },
        flow: [
            { icon: Sprout, label: "Renewable by-products", sub: "Plant-based polymers with natural fillers like date-seed particles." },
            { icon: Boxes, label: "Pellet & filament engineering", sub: "Tuned for FDM/FFF printers and robotic arms." },
            { icon: Bot, label: "Biodegradable products", sub: "Design, prototyping and non-structural components." },
        ],
        features: [
            { icon: Sprout, title: "Bio-Based Pellets", desc: "Biocomposite pellets for FGF and extrusion." },
            { icon: Layers, title: "Printing Filament", desc: "FDM/FFF-ready filament with a natural matte finish." },
            { icon: Bot, title: "Robotic 3D Printing", desc: "Large-format additive manufacturing." },
            { icon: Package, title: "Applications", desc: "Decoration, sustainable design and prototyping." },
        ],
        stats: ["Certified Compostable", "FDM & FGF Ready", "Renewable Feedstock", "Low-Waste Production"],
    },
    "it-system-integration": {
        n: "06",
        overline: "Solution 06 — Digital Operations",
        title: "IT & System Integration Solutions",
        lead: "Enterprise software integration, ERP/CRM connectivity and automation — connecting operations end to end.",
        challenge:
            "Operations run on disconnected systems — data sits in silos and decisions arrive late.",
        approach:
            "We integrate tracking, pricing, logistics and partner systems into one connected operational stack.",
        flow: [
            { icon: Plug, label: "Operational systems", sub: "ERP, CRM, fleet and site systems connected." },
            { icon: Workflow, label: "Integration & automation", sub: "Data flows automated between every system." },
            { icon: CheckCircle2, label: "One connected operation", sub: "Live visibility, faster decisions, lower cost." },
        ],
        features: [
            { icon: LayoutDashboard, title: "Digital Tracking Dashboards", desc: "Live waste, fleet and facility dashboards — every stream visible." },
            { icon: TrendingUp, title: "Pricing & Market Data Systems", desc: "Market-linked pricing data built into your operations." },
            { icon: Route, title: "Logistics & Fleet Systems", desc: "Connected routing that cuts cost, time and emissions." },
            { icon: Network, title: "Supplier–Client Portals", desc: "Portals that match suppliers, processors and buyers automatically." },
        ],
        stats: ["ERP / CRM Connectivity", "Live Dashboards", "End-to-End Automation", "API-Led Integration"],
    },
};

export const DOMAIN_CARDS = [
    { slug: "facility-management", icon: Building2, title: "Total Facility Management", desc: "AMC, MEP & HVAC, security, cleaning and technical services — one partner." },
    { slug: "waste-recovery", icon: Recycle, title: "Waste Recovery Solutions", desc: "Food waste, dry general waste and hazardous waste — treated and recovered responsibly." },
    { slug: "alva-oliva", icon: Droplets, title: "Alva Oliva — Extra Virgin Olive Oil", desc: "Authorized GCC distributor of premium Spanish EVOO." },
    { slug: "uco-sourcing", icon: Leaf, title: "UCO Sourcing & Supply", desc: "International sourcing agent for ISCC-certified used cooking oil." },
    { slug: "advanced-materials", icon: Boxes, title: "Advanced Materials", desc: "Bio-based pellets and filament for 3D printing." },
    { slug: "it-system-integration", icon: Workflow, title: "IT & System Integration", desc: "ERP/CRM connectivity, dashboards and automation for operations." },
];
